import { usePrefs } from '../prefs.js'
import { EN } from './en.js'

/**
 * Übersetzung der Oberfläche.
 *
 * ## Warum der deutsche Satz der Schlüssel ist
 *
 * Üblich wäre `t('chat.send')` mit zwei Wörterbüchern. Hier steht stattdessen
 * `t('Nachricht schreiben…')` — der deutsche Text IST der Schlüssel, und nur
 * die englische Fassung liegt in einer Tabelle.
 *
 * Der Grund ist diese Codebasis: Sie ist durchgehend deutsch, samt Kommentaren
 * und Dokumentation, und sie wird von Menschen gelesen, die Deutsch sprechen.
 * Ein Schlüssel wie `chat.send.placeholder` macht jede Stelle unlesbar, an der
 * man sonst sofort sieht, was dort steht — und er erzeugt eine zweite Sorte
 * Fehler: einen Schlüssel, der nirgends definiert ist und zur Laufzeit als
 * `chat.send.placeholder` auf dem Bildschirm landet.
 *
 * Hier kann das nicht passieren: Fehlt ein Eintrag, steht der deutsche Satz da.
 * Das ist die richtige Rückfallebene — verständlich, nur eben nicht übersetzt.
 *
 * ## Und warum es trotzdem eine Prüfung gibt
 *
 * Eine stille Rückfallebene ist genau die Sorte Zusage, die nach drei Monaten
 * nicht mehr gilt: Die App wäre halb englisch, und niemandem fiele es auf, weil
 * nichts kaputtgeht. `scripts/check-i18n.mjs` sammelt deshalb jeden Aufruf von
 * `t()` aus dem Quelltext und lässt den Build scheitern, wenn einer davon in
 * `en.ts` fehlt — oder wenn dort ein Eintrag steht, den niemand mehr benutzt.
 *
 * ## Platzhalter
 *
 *   t('{n} neue Nachrichten', { n: 3 })
 *
 * Absichtlich schlicht: keine Pluralregeln, keine Formatierung. Wo eine
 * Mehrzahl anders lauten muss, stehen zwei Sätze nebeneinander und eine
 * Fallunterscheidung davor — das liest sich schlechter im Code und besser auf
 * dem Bildschirm.
 */

export type Werte = Record<string, string | number>

function einsetzen(text: string, werte: Werte | undefined): string {
  if (!werte) return text
  return text.replace(/\{(\w+)\}/g, (ganz, name: string) =>
    name in werte ? String(werte[name]) : ganz)
}

/**
 * Übersetzt, wenn Englisch eingestellt ist.
 *
 * Liest die Sprache direkt aus dem Store statt über einen Hook: `t()` wird
 * auch außerhalb von Komponenten aufgerufen (in Fehlerpfaden, in Hilfsfunktionen).
 * Dass die Oberfläche beim Umschalten trotzdem vollständig neu aufgebaut wird,
 * erledigt `App.tsx` mit einem `key` — Begründung steht dort.
 */
export function t(deutsch: string, werte?: Werte): string {
  const sprache = usePrefs.getState().sprache
  if (sprache === 'de') return einsetzen(deutsch, werte)
  return einsetzen(EN[deutsch] ?? deutsch, werte)
}

/** Für Komponenten, die sich beim Sprachwechsel selbst neu zeichnen sollen. */
export function useSprache() {
  return usePrefs((s) => s.sprache)
}

/**
 * Markiert einen deutschen Text in einer **Datenstruktur** als übersetzbar.
 *
 * Zur Laufzeit tut das nichts — es gibt den Text unverändert zurück. Der Zweck
 * ist die Prüfung: `t(item.label)` kann `check-i18n.mjs` nicht auswerten, weil
 * dort erst zur Laufzeit ein Text steht. Die Beschriftungen der Reiter, die
 * Rollennamen und die Statusoptionen stehen aber als Literale in ihren
 * Datenstrukturen — mit `text(…)` markiert, findet die Prüfung sie dort und
 * kann verlangen, dass es eine englische Fassung gibt.
 *
 * Regel dahinter: Jeder Text, der irgendwann durch `t()` läuft, muss
 * IRGENDWO als Literal stehen — entweder direkt in `t('…')` oder in `text('…')`.
 * Was diese Prüfung nicht sieht, ist beim Umschalten deutsch.
 */
export const text = (deutsch: string): string => deutsch
